import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  PrincipalFamilyMember,
  PrincipalResidence,
  PrincipalRiskLevel,
  PrincipalVehicle,
  UpdatePrincipalInput,
} from '@dsr/shared-types';
import {
  useDeletePrincipal,
  usePrincipal,
  useUpdatePrincipal,
} from '../api/queries/principals';
import {
  usePerson,
  usePersonHistory,
  useRevealPersonGovernmentId,
} from '../api/queries/persons';
import { PersonDetailView } from '../components/PersonDetailView';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Textarea,
} from '../components/ui';
import { can } from '../auth/store';
import { ApiError } from '../api/client';

const RISK_LEVELS: PrincipalRiskLevel[] = [
  'low',
  'guarded',
  'elevated',
  'high',
  'severe',
];

function fmtDate(epoch: number | null): string {
  if (epoch === null) return '—';
  return new Date(epoch).toLocaleDateString('en-US');
}

export function PrincipalDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const principalId = id ? Number(id) : null;

  // The whole detail view exposes heavy PII (DOB, medical notes, residences),
  // so it is gated on principal.read. Without it we never even fire the query.
  const canRead = can('principal.read');
  const { data, isLoading, isError, error } = usePrincipal(
    canRead ? principalId : null,
  );

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeletePrincipal();

  const canWrite = can('principal.write');
  const canDelete = can('principal.delete');

  if (!canRead) {
    return (
      <p className="error-text" role="alert">
        You do not have permission to view principal records.
      </p>
    );
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="error-text" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load principal.'}
      </p>
    );
  }

  const p = data.principal;

  const onDelete = async () => {
    if (principalId === null) return;
    try {
      await del.mutateAsync(principalId);
      toast.success('Principal deleted.');
      setConfirmDelete(false);
    } catch (err) {
      toast.danger(
        err instanceof ApiError ? err.message : 'Failed to delete principal.',
      );
    }
  };

  return (
    <div className="page principal-detail-page">
      <nav className="breadcrumbs">
        <Link to="/directory/individuals">Individuals</Link>
        <span aria-hidden="true"> / </span>
        <span>{p.full_name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1>{p.full_name}</h1>
          <p className="page-sub">
            <StatusBadge status={`risk-${p.risk_level}`}>{p.risk_level}</StatusBadge>{' '}
            {p.alias ? `aka ${p.alias}` : ''}
          </p>
        </div>
        <div className="header-actions">
          {canWrite && (
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      {/* PII overview — only rendered because canRead gated the whole page. */}
      <section className="detail-card">
        <h2>Overview</h2>
        <dl className="detail-grid">
          <div>
            <dt>Alias</dt>
            <dd>{p.alias ?? '—'}</dd>
          </div>
          <div>
            <dt>Date of birth</dt>
            <dd>{fmtDate(p.dob)}</dd>
          </div>
          <div>
            <dt>Risk level</dt>
            <dd>{p.risk_level}</dd>
          </div>
          <div>
            <dt>Client</dt>
            <dd>
              {p.organization_id ? (
                <Link to={`/organizations/${p.organization_id}`}>
                  #{p.organization_id}
                </Link>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div className="detail-grid-full">
            <dt>Medical notes</dt>
            <dd className="pii-sensitive">{p.medical_notes ?? '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="detail-card">
        <h2>Family members</h2>
        {p.family_members.length === 0 ? (
          <p className="muted">None recorded.</p>
        ) : (
          <ul className="record-list">
            {p.family_members.map((m, i) => (
              <li key={i}>
                <strong>{m.name ?? 'Unnamed'}</strong>
                {m.relationship ? ` · ${m.relationship}` : ''}
                {m.notes ? <div className="muted small">{m.notes}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="detail-card">
        <h2>Residences</h2>
        {p.residences.length === 0 ? (
          <p className="muted">None recorded.</p>
        ) : (
          <ul className="record-list">
            {p.residences.map((r, i) => (
              <li key={i}>
                <strong>{r.label ?? 'Residence'}</strong>
                {r.address ? <div>{r.address}</div> : null}
                {r.lat !== undefined && r.lng !== undefined ? (
                  <div className="muted small">
                    {r.lat}, {r.lng}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="detail-card">
        <h2>Vehicles</h2>
        {p.vehicles.length === 0 ? (
          <p className="muted">None recorded.</p>
        ) : (
          <ul className="record-list">
            {p.vehicles.map((v, i) => (
              <li key={i}>
                <strong>
                  {[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                </strong>
                {v.color ? ` · ${v.color}` : ''}
                {v.plate ? (
                  <div className="muted small">Plate: {v.plate}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {p.person_id != null && p.person_id > 0 && (
        <PersonProfileSection personId={p.person_id} />
      )}

      {editOpen && (
        <EditPrincipalModal
          principalId={p.id}
          initial={{
            full_name: p.full_name,
            alias: p.alias,
            dob: p.dob,
            risk_level: p.risk_level,
            medical_notes: p.medical_notes,
            family_members: p.family_members,
            residences: p.residences,
            vehicles: p.vehicles,
          }}
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete principal"
        message={`Delete "${p.full_name}"? This soft-deletes the principal record.`}
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linked person-registry identity — rendered via the shared PersonDetailView.
// History/government-ID reveal are gated by can() and only fetched when the
// viewer holds the matching permission (same gating as personnel detail).
// The residences / family / vehicles editors above keep working against the
// principal's legacy-shaped JSONB aliases; this card is purely additive.
// ---------------------------------------------------------------------------

function PersonProfileSection({ personId }: { personId: number }): JSX.Element {
  const { data, isLoading, isError } = usePerson(personId);
  const canHistory = can('person.read_history');
  const canGovIds = can('person.read_government_ids');
  const { data: historyData } = usePersonHistory(personId, canHistory);
  const reveal = useRevealPersonGovernmentId();

  const onRevealGovernmentId = canGovIds
    ? async (govId: number): Promise<string> => {
        const res = await reveal.mutateAsync({ personId, govId });
        return res.number;
      }
    : undefined;

  return (
    <section className="detail-card">
      <h2>Person profile</h2>
      {isLoading && <p className="muted">Loading person profile…</p>}
      {isError && <p className="muted">Failed to load the linked person profile.</p>}
      {!isLoading && !isError && data?.person && (
        <PersonDetailView
          person={data.person}
          history={historyData?.history ?? null}
          onRevealGovernmentId={onRevealGovernmentId}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit principal — scalar fields plus simple repeating-row editors for the
// JSONB subrecords (family members, residences, vehicles).
// ---------------------------------------------------------------------------

function EditPrincipalModal({
  principalId,
  initial,
  onClose,
}: {
  principalId: number;
  initial: {
    full_name: string;
    alias: string | null;
    dob: number | null;
    risk_level: PrincipalRiskLevel;
    medical_notes: string | null;
    family_members: PrincipalFamilyMember[];
    residences: PrincipalResidence[];
    vehicles: PrincipalVehicle[];
  };
  onClose: () => void;
}): JSX.Element {
  const [fullName, setFullName] = useState(initial.full_name);
  const [alias, setAlias] = useState(initial.alias ?? '');
  const [dob, setDob] = useState<number | null>(initial.dob);
  const [riskLevel, setRiskLevel] = useState<PrincipalRiskLevel>(initial.risk_level);
  const [medicalNotes, setMedicalNotes] = useState(initial.medical_notes ?? '');
  const [family, setFamily] = useState<PrincipalFamilyMember[]>(
    initial.family_members,
  );
  const [residences, setResidences] = useState<PrincipalResidence[]>(
    initial.residences,
  );
  const [vehicles, setVehicles] = useState<PrincipalVehicle[]>(initial.vehicles);

  const update = useUpdatePrincipal();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const patch: UpdatePrincipalInput = {
      full_name: fullName.trim(),
      alias: alias.trim() || null,
      dob,
      risk_level: riskLevel,
      medical_notes: medicalNotes.trim() || null,
      family_members: family.filter((m) => (m.name ?? '').trim().length > 0),
      residences: residences.filter(
        (r) => (r.label ?? '').trim().length > 0 || (r.address ?? '').trim().length > 0,
      ),
      vehicles: vehicles.filter(
        (v) =>
          (v.make ?? '').trim().length > 0 ||
          (v.model ?? '').trim().length > 0 ||
          (v.plate ?? '').trim().length > 0,
      ),
    };

    try {
      await update.mutateAsync({ id: principalId, ...patch });
      toast.success('Principal updated.');
      onClose();
    } catch (err) {
      toast.danger(
        err instanceof ApiError ? err.message : 'Failed to update principal.',
      );
    }
  };

  return (
    <Dialog
      title="Edit principal"
      open
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-principal-form"
            variant="primary"
            disabled={update.isPending || fullName.trim().length === 0}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="edit-principal-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Full name" required>
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Alias">
          <Input value={alias} onChange={(e) => setAlias(e.target.value)} />
        </FormField>
        <FormField label="Risk level">
          <select
            className="form-input"
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value as PrincipalRiskLevel)}
          >
            {RISK_LEVELS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Date of birth">
          <DateInput value={dob} onChange={setDob} />
        </FormField>
        <FormField label="Medical notes" full>
          <Textarea
            rows={3}
            value={medicalNotes}
            onChange={(e) => setMedicalNotes(e.target.value)}
          />
        </FormField>

        {/* Family members */}
        <fieldset className="form-field-full subrecord-fieldset">
          <legend>Family members</legend>
          {family.map((m, i) => (
            <div className="subrecord-row" key={i}>
              <Input
                placeholder="Name"
                value={m.name ?? ''}
                onChange={(e) =>
                  setFamily((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, name: e.target.value } : x,
                    ),
                  )
                }
              />
              <Input
                placeholder="Relationship"
                value={m.relationship ?? ''}
                onChange={(e) =>
                  setFamily((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, relationship: e.target.value } : x,
                    ),
                  )
                }
              />
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setFamily((prev) => prev.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setFamily((prev) => [...prev, {}])}
          >
            Add family member
          </Button>
        </fieldset>

        {/* Residences */}
        <fieldset className="form-field-full subrecord-fieldset">
          <legend>Residences</legend>
          {residences.map((r, i) => (
            <div className="subrecord-row" key={i}>
              <Input
                placeholder="Label"
                value={r.label ?? ''}
                onChange={(e) =>
                  setResidences((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, label: e.target.value } : x,
                    ),
                  )
                }
              />
              <Input
                placeholder="Address"
                value={r.address ?? ''}
                onChange={(e) =>
                  setResidences((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, address: e.target.value } : x,
                    ),
                  )
                }
              />
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() =>
                  setResidences((prev) => prev.filter((_, j) => j !== i))
                }
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setResidences((prev) => [...prev, {}])}
          >
            Add residence
          </Button>
        </fieldset>

        {/* Vehicles */}
        <fieldset className="form-field-full subrecord-fieldset">
          <legend>Vehicles</legend>
          {vehicles.map((v, i) => (
            <div className="subrecord-row" key={i}>
              <Input
                placeholder="Make"
                value={v.make ?? ''}
                onChange={(e) =>
                  setVehicles((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, make: e.target.value } : x,
                    ),
                  )
                }
              />
              <Input
                placeholder="Model"
                value={v.model ?? ''}
                onChange={(e) =>
                  setVehicles((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, model: e.target.value } : x,
                    ),
                  )
                }
              />
              <Input
                placeholder="Plate"
                value={v.plate ?? ''}
                onChange={(e) =>
                  setVehicles((prev) =>
                    prev.map((x, j) =>
                      j === i ? { ...x, plate: e.target.value } : x,
                    ),
                  )
                }
              />
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setVehicles((prev) => prev.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setVehicles((prev) => [...prev, {}])}
          >
            Add vehicle
          </Button>
        </fieldset>
      </form>
    </Dialog>
  );
}
